import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AdornedPercentageInput from '@/components/common/AdornedPercentageInput';
import AdornedCurrencyInput from '@/components/common/AdornedCurrencyInput';
import AccountTagsAutocomplete from '@/components/common/AccountTagsAutocomplete';
import CategoryUtility, { ClassificationItem } from '../categories.utility';
import { Classification } from '@/store/classificationsApi';
import { useEffect, useMemo, useRef } from 'react';
import {
  AccountDetailsFormState,
  AccountDetailsFormValues,
} from './useAccountDetailsForm';
import { useLocalization } from '@/hooks/useLocalization';
import { useAccountTagCatalogOptions } from '@/hooks/useAccountTagCatalogOptions';
import { useAttributeAccountsToViewingTag } from '@/hooks/useAttributeAccountsToViewingTag';
import { mergeSelectedViewingTagOption } from '@/modules/portfolio/accountTags/accountTagCatalog';
import { nextSelectedTagIdsForViewingTagSeed } from '@/utils/viewingTagAccountAttribution';

interface AccountDetailsFormProps {
  initialValues: AccountDetailsFormValues;
  selectedClassification: ClassificationItem;
  classifications: Classification[];
  onFormChange: (state: AccountDetailsFormState) => void;
}

const AccountDetailsForm = ({
  initialValues,
  selectedClassification,
  classifications,
  onFormChange,
}: AccountDetailsFormProps) => {
  const { t } = useLocalization();
  const { options: catalogTagOptions, isLoading: isLoadingTags } =
    useAccountTagCatalogOptions();
  const { attributableTag, attributableTagId } =
    useAttributeAccountsToViewingTag();
  const hasSeededViewingTagRef = useRef(false);

  const tagOptions = useMemo(
    () => mergeSelectedViewingTagOption(catalogTagOptions, attributableTag),
    [catalogTagOptions, attributableTag],
  );

  const presetSubClassificationId =
    selectedClassification.presetSubClassificationId;
  const subCategories = presetSubClassificationId
    ? []
    : CategoryUtility.getSubcategories(selectedClassification, classifications);

  const validationSchema = Yup.object({
    Name: Yup.string().required(t('addAccountModal.accountDetails.required')),
    AccountNumber: Yup.string().optional(),
    AccountBalance: Yup.number()
      .optional()
      .when([], {
        is: () => selectedClassification.isLiability,
        then: (schema) =>
          schema.max(0, t('addAccountModal.accountDetails.negative')),
        otherwise: (schema) =>
          schema.min(0, t('addAccountModal.accountDetails.positive')),
      }),
    Notes: Yup.string().optional(),
    AccountSubType: Yup.string().when([], {
      is: () => subCategories.length > 0 && !presetSubClassificationId,
      then: (schema) =>
        schema.required(t('addAccountModal.accountDetails.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    MonthlyPaymentAmount: Yup.string().optional(),
    InitialLoanAmount: Yup.string().optional(),
    InterestRate: Yup.string().optional(),
    ClassificationId: Yup.string().required(),
    ParentId: Yup.string().required(),
    SelectedTagIds: Yup.array().of(Yup.number()).optional(),
  });

  const accountForm = useFormik({
    initialValues: {
      ...initialValues,
      ClassificationId: selectedClassification.ClassificationId,
      ParentId: selectedClassification.ParentId,
      SelectedTagIds:
        initialValues.SelectedTagIds.length > 0
          ? initialValues.SelectedTagIds
          : attributableTagId != null
            ? [attributableTagId]
            : [],
    },
    validationSchema,
    onSubmit: () => {},
    initialErrors: {
      Name: t('addAccountModal.accountDetails.required'),
    }, // Not visible to the user, but disables the "Continue" button on load.
    validateOnMount: true,
    validateOnChange: true,
  });

  useEffect(() => {
    if (!presetSubClassificationId) return;
    void accountForm.setFieldValue('AccountSubType', presetSubClassificationId);
    void accountForm.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match legacy subtype preset sync
  }, [
    presetSubClassificationId,
    accountForm.setFieldValue,
    accountForm.validateForm,
  ]);

  // Prefill Tags from an assignable TagSelectorDropdown value so a filtered
  // create stays visible in the account list. View-only tags are skipped.
  // The user may still clear the prefilled tag.
  useEffect(() => {
    const { nextIds, seeded } = nextSelectedTagIdsForViewingTagSeed({
      hasSeeded: hasSeededViewingTagRef.current,
      viewingTagId: attributableTagId,
      currentIds: accountForm.values.SelectedTagIds,
    });
    hasSeededViewingTagRef.current = seeded;
    if (nextIds != null) {
      void accountForm.setFieldValue('SelectedTagIds', nextIds, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once from viewing-tag filter
  }, [
    attributableTagId,
    accountForm.values.SelectedTagIds,
    accountForm.setFieldValue,
  ]);

  // Keep parent state in sync with form values (including optional SelectedTagIds).
  useEffect(() => {
    onFormChange({
      values: accountForm.values,
      isValid: accountForm.isValid,
    });
  }, [accountForm.values, accountForm.isValid, onFormChange]);

  const handleCurrencyChange = async (
    field: 'AccountBalance' | 'MonthlyPaymentAmount' | 'InitialLoanAmount',
    value: string | undefined,
  ) => {
    await accountForm.setFieldValue(field, value);
  };

  const handlePercentageChange = async (
    field: 'InterestRate',
    value: string | undefined,
  ) => {
    await accountForm.setFieldValue(field, value);
  };

  const handleSelectChange = async (
    field: 'AccountSubType',
    value: number | undefined,
  ) => {
    await accountForm.setFieldValue(field, value);
    await accountForm.validateForm(); // Manually validate form
  };

  return (
    <form onSubmit={accountForm.handleSubmit}>
      <Box sx={{ marginBottom: 4 }}>
        <Grid
          container
          rowSpacing={3}
          columnSpacing={3}
        >
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={t('addAccountModal.accountDetails.accountName.label')}
              placeholder={t(
                'addAccountModal.accountDetails.accountName.placeholder',
              )}
              required
              error={accountForm.touched.Name && !!accountForm.errors.Name}
              {...accountForm.getFieldProps('Name')}
            />
            {accountForm.touched.Name && accountForm.errors.Name && (
              <FormHelperText error>{accountForm.errors.Name}</FormHelperText>
            )}
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('addAccountModal.accountDetails.accountNumber.label')}
              placeholder={t(
                'addAccountModal.accountDetails.accountNumber.placeholder',
              )}
              {...accountForm.getFieldProps('AccountNumber')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <AdornedCurrencyInput
              allowDecimals
              allowNegativeValue={selectedClassification.isLiability}
              decimalsLimit={2}
              fullWidth
              label={t('addAccountModal.accountDetails.accountBalance.label')}
              value={accountForm.values.AccountBalance}
              onValueChange={(event) => {
                void handleCurrencyChange('AccountBalance', event);
              }}
            />
            {accountForm.errors.AccountBalance && (
              <FormHelperText error>
                {accountForm.errors.AccountBalance}
              </FormHelperText>
            )}
          </Grid>

          {/* ONLY if sub-types */}
          {subCategories.length > 0 && !presetSubClassificationId && (
            <Grid size={{ xs: 12 }}>
              <FormControl
                fullWidth
                required
                error={
                  accountForm.touched.AccountSubType &&
                  Boolean(accountForm.errors.AccountSubType)
                }
              >
                <InputLabel id='subtype-label'>
                  {t('addAccountModal.accountDetails.accountSubtype.label', {
                    name: selectedClassification.Name,
                  })}
                </InputLabel>
                <Select
                  labelId='subtype-label'
                  id='subtype'
                  label={t(
                    'addAccountModal.accountDetails.accountSubtype.label',
                    {
                      name: selectedClassification.Name,
                    },
                  )}
                  {...accountForm.getFieldProps('AccountSubType')}
                  onChange={(event) => {
                    void handleSelectChange(
                      'AccountSubType',
                      Number(event.target.value),
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        paddingBottom:
                          'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
                      },
                    },
                  }}
                >
                  {subCategories.map((subCategory) => (
                    <MenuItem
                      value={subCategory.ClassificationId}
                      key={subCategory.ClassificationId}
                    >
                      {subCategory.Name}
                    </MenuItem>
                  ))}
                </Select>
                {accountForm.touched.AccountSubType &&
                  Boolean(accountForm.errors.AccountSubType) && (
                    <FormHelperText error>
                      {accountForm.errors.AccountSubType}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <AccountTagsAutocomplete
              options={tagOptions}
              selectedTagIds={accountForm.values.SelectedTagIds}
              isLoading={isLoadingTags}
              onChange={(tagIds) => {
                void accountForm.setFieldValue('SelectedTagIds', tagIds);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={t('addAccountModal.accountDetails.notes.label')}
              multiline
              placeholder={t(
                'addAccountModal.accountDetails.notes.placeholder',
              )}
              rows={3}
              {...accountForm.getFieldProps('Notes')}
            />
          </Grid>

          {/* Liability ONLY */}
          {selectedClassification.isLiability && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <AdornedCurrencyInput
                  allowDecimals
                  decimalsLimit={2}
                  fullWidth
                  label={t(
                    'addAccountModal.accountDetails.monthlyPaymentAmount.label',
                  )}
                  value={accountForm.values.MonthlyPaymentAmount}
                  onValueChange={(event) => {
                    void handleCurrencyChange('MonthlyPaymentAmount', event);
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <AdornedCurrencyInput
                  allowDecimals
                  decimalsLimit={2}
                  fullWidth
                  label={t(
                    'addAccountModal.accountDetails.initialLoanAmount.label',
                  )}
                  value={accountForm.values.InitialLoanAmount}
                  onValueChange={(event) => {
                    void handleCurrencyChange('InitialLoanAmount', event);
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <AdornedPercentageInput
                  allowDecimals
                  decimalsLimit={3}
                  fullWidth
                  label={t('addAccountModal.accountDetails.interestRate.label')}
                  allowNegativeValue={false}
                  placeholder={t(
                    'addAccountModal.accountDetails.interestRate.placeholder',
                  )}
                  onValueChange={(event) => {
                    void handlePercentageChange('InterestRate', event);
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </form>
  );
};

export default AccountDetailsForm;
